import React, { createContext, useContext, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { Segment } from 'semantic-ui-react';
import styled from 'styled-components';
import GrassIcon from '@assets/svgs/klima/Icon_Gras.svg'
import MaizeIcon from '@assets/svgs/klima/Icon_Mais.svg'
import GrassSilage from './GrassSilage';
import CornSilage from './CornSilage';
import { ClimaCheckOptionsType } from '~/api/services/climaCheck';
import AnimatedArrow from './AnimationArrow';
import { FeedPageContext } from '../..';

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StepText = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #4a5568;

  div {
    font-size: 16px !important;
    font-weight: bold;
  }

  i {
    color: ${props => props.theme?.firstColor};
    cursor: pointer;
    font-size: 2em !important;
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  gap: 8px;
  margin: 10px 0 20px;
`;

const StepIndicator = styled.div<{ $active: boolean, $completed: boolean }>`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background-color: ${props => (props.$active || props.$completed ? '#22c55e' : '#e5e7eb')};
  filter: ${props => props.$completed ? 'opacity(0.5)' : 'none'};
`;

const OptionGroup = styled.div`
  display: flex;
  gap: 10px;
  .ui.segment:first-child {
    margin-bottom: 1em !important;
    margin-top: 1em !important;
  }

   .ui.segment:last-child {
    margin-bottom: 1em !important;
    margin-top: 1em !important;
  }
`;

const OptionButton = styled(Segment)<{ $active?: boolean }>`
  flex: 1;
  cursor: pointer;
  display: flex;
  padding: 0.4rem !important;
  font-size: 15px !important;
  align-items: center;
  justify-content: center;
  font-weight: ${props => (props.$active ? 'bold' : 'normal')};
  background-color: ${props => (props.$active ? '#fff' : '#f9fafb')};
  border: ${props => props.$active ? '2px solid #22c55e' : '2px solid rgba(34,36,38,.15)'} !important;
  img {
   width: 23px;
  }
`;

export const NO_SILAGE_VALUE = 1
const CORN_SILAGE_VALUE = 2
const GRASS_SILAGE_VALUE = 3
const CORN_GRASS_SILAGE_VALUE = 4

interface Props {
  options: ClimaCheckOptionsType
}

const grassSilageRequiredFields = {
  0: [
    'grassSilage.locationFavorability',
    'grassSilage.fertilizationType',
    'grassSilage.cuttingFrequency',
    'grassSilage.dryMatter',
    'grassSilage.freshMassYield'
  ],
  1: [
    'grassSilage.organicFertilizers'
  ],
  2: [
    'grassSilage.mineralFertilizers',
    'grassSilage.quicklimeAmount',
    'grassSilage.limestoneAmount'
  ],
  3: [
    'grassSilage.dieselConsumption',
    'grassSilage.biodieselPercentage',
    'grassSilage.irrigation',
    'grassSilage.irrigationWaterUsed',
    'grassSilage.irrigationMethod',
    'grassSilage.seedUsed',
    'grassSilage.irrigationDieselUsed',
    'grassSilage.irrigationElectricityUsed',
    'grassSilage.plantProtections'
  ]
}

const maizeSilageRequiredFields = {
  0: [
    'maizeSilage.harvestingMethodId',
    'maizeSilage.maturityLevelId',
    'maizeSilage.grainContent',
    'maizeSilage.dryMatter',
    'maizeSilage.freshMassYield'
  ],
  1: [
    'maizeSilage.organicFertilizers'
  ],
  2: [
    'maizeSilage.mineralFertilizers',
    'maizeSilage.quicklimeAmount',
    'maizeSilage.limestoneAmount'
  ],
  3: [
    'maizeSilage.dieselConsumption',
    'maizeSilage.biodieselPercentage',
    'maizeSilage.irrigation',
    'maizeSilage.irrigationWaterUsed',
    'maizeSilage.irrigationMethod',
    'maizeSilage.seedUsed',
    'maizeSilage.irrigationDieselUsed',
    'maizeSilage.irrigationElectricityUsed',
    'maizeSilage.plantProtections'
  ]
  
}

type ExtendedClimaCheckOptionsType = ClimaCheckOptionsType & {
  handleNext: () => void;
  handlePrevious: () => void;
};

export const ClimaOptionsContext = createContext<ExtendedClimaCheckOptionsType>(null)

const ForageProduction:React.FC<Props> = ({ options }) => {
  const { control, setValue, getValues, setError } = useFormContext()
  const { handleSubmit } = useContext(FeedPageContext)
  const watchGrowGrassOrMaize = useWatch({ control, name : 'growSilage' })

  const [activeStep, setActiveStep] = useState(0)

  const [steps, setSteps] = useState([1, 2, 3, 4])

  useEffect(() => {
    if(watchGrowGrassOrMaize === CORN_GRASS_SILAGE_VALUE) {
      setSteps([1, 2, 3, 4, 5, 6, 7, 8])
    }
  }, [])

  function renderMaiseGrassSelection() {
    const showCornSilage = grassAndMaizeIncluded && activeStep <= 3
    const showGrassSilage = grassAndMaizeIncluded && activeStep >= 4
    return <OptionGroup>
      {showCornSilage && <OptionButton
        $active={true}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div>
            <img src={MaizeIcon} />
          </div>
          <div>{'Daten für Maissilage'}</div>
        </div>
      </OptionButton>}

      {showGrassSilage && <OptionButton $active={true}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div>
            <img src={GrassIcon} />
          </div>
          <div>{'Daten für Grassilage'}</div>
        </div>
      </OptionButton>}
    </OptionGroup>
  }

  function validateFields(values) {
    const formData = getValues()
    return values.filter((fieldKey:string) => {
      const value = fieldKey.split('.').reduce((obj, key) => obj?.[key], formData)

      //array fields
      if(fieldKey.includes('organicFertilizers')) {

        //first select
        if(!value || value?.length === 0) {
          const targetElement = document.getElementById(fieldKey)
          targetElement.classList.add('error');
          return fieldKey
        }

        // organic fields inside table
        const errors = value.filter((organicFertilizer, index) => {
          if(!organicFertilizer.value) {
            const name = `${fieldKey}[${index}].value`
            setError(name, { message: '' })
            return fieldKey
          } else if(!organicFertilizer.organicFertilizerApplicationTechniqueId) {
            setError(`${fieldKey}[${index}].organicFertilizerApplicationTechniqueId`, { message: '' })
            return fieldKey
          }
        })

        if(errors.length > 0) {
          return fieldKey
        }
      } else if (fieldKey.includes('mineralFertilizers')) {
        //first select
        if(!value || value?.length === 0) {
          const targetElement = document.getElementById(fieldKey)
          targetElement.classList.add('error');
          return fieldKey
        }
  
        // organic fields inside table
        const errors = value.filter((organicFertilizer, index) => {
          if(!organicFertilizer.value) {
            const name = `${fieldKey}[${index}].value`
            setError(name, { message: '' })
            return fieldKey
          } else if(!organicFertilizer.mineralFertilizerApplicationTechniqueId) {
            setError(`${fieldKey}[${index}].mineralFertilizerApplicationTechniqueId`, { message: '' })
            return fieldKey
          }
        })

        if(errors?.length > 0) {
          return fieldKey
        }
      } else if (fieldKey.includes('plantProtections')) {
        // protection fields inside card
        const errors = value?.filter((plantProtection, index) => {
          if(!plantProtection.number) {
            const name = `${fieldKey}[${index}].number`
            setError(name, { message: '' })
            return fieldKey
          } else if(!plantProtection.waterUsed) {
            setError(`${fieldKey}[${index}].waterUsed`, { message: '' })
            return fieldKey
          }
        })

        if(errors?.length > 0) {
          return fieldKey
        }
      }
      else {
        const isFieldDisplayed = Boolean(document.getElementById(fieldKey))
        if(!value && isFieldDisplayed) {
          setError(fieldKey, { message: '' })
          return fieldKey
        }
      }
    })
  }

  function handleNext() {
    const indexFields = grassAndMaizeIncluded ? activeStep % 4 : activeStep
    const arrayFields = showCorn ? maizeSilageRequiredFields : grassSilageRequiredFields

    const errorFields = validateFields(arrayFields[indexFields])

    if(errorFields?.length === 0 && (activeStep < steps.length - 1)) {
      setActiveStep(activeStep + 1)
    } else if (errorFields?.length === 0 && (activeStep === steps.length - 1)) {
      handleSubmit()
    }
  }

  function handlePrevious() {
    setActiveStep(activeStep - 1)
  }

  const grassAndMaizeIncluded = watchGrowGrassOrMaize === CORN_GRASS_SILAGE_VALUE
  const showCorn = watchGrowGrassOrMaize === GRASS_SILAGE_VALUE || (grassAndMaizeIncluded && activeStep <= 3)
  const showGrass = watchGrowGrassOrMaize === CORN_SILAGE_VALUE || (grassAndMaizeIncluded && activeStep >= 4)

  const cornOrGrassSelected = watchGrowGrassOrMaize && watchGrowGrassOrMaize !== 1

  return <>
    <Container>
      <TitleRow>
        <FormFields.Select
          label={'Bauen Sie selber Grassilage oder Maissilage an?'}
          placeholder={'Bitte auswählen'}
          control={control}
          onChange={(e, { value }) => {
            if(value === CORN_GRASS_SILAGE_VALUE) {
              setSteps([1, 2, 3, 4, 5, 6, 7, 8])
            } else if(value !== NO_SILAGE_VALUE) {
              setSteps([1, 2, 3, 4])
            }
            setValue('grassSilage', null)
            setValue('maizeSilage', null)
            setValue('growSilage', value)
            setActiveStep(0)
          }}
          name='growSilage'
          rules={{
            required: true
          }}
          style={{ width: 300 }}
          selectOnBlur={false}
          withoutAsterisk
          noErrorMessage
          options={options.growSilageTypes?.map(type => ({ value: type.key, text: type.label })) || []}
        />
      </TitleRow>

      {cornOrGrassSelected && <ProgressContainer>
        {steps.map((_, index) => (
          <StepIndicator
            key={index}
            $active={index === activeStep}
            $completed={index < activeStep}
          />
        ))}
      </ProgressContainer> }

      {grassAndMaizeIncluded && renderMaiseGrassSelection()}

      {cornOrGrassSelected && <ClimaOptionsContext.Provider value={{ ...options, handleNext, handlePrevious }}>
        {showCorn && <CornSilage activeStep={activeStep}/>}
        {showGrass && <GrassSilage
          activeStep={grassAndMaizeIncluded ? activeStep % 4 : activeStep}
        />}
      </ClimaOptionsContext.Provider>}
      <div style={{ display: 'flex', justifyContent: 'end', marginTop: '2em' }}>
        {cornOrGrassSelected && <StepText>
          {activeStep > 0 && <AnimatedArrow
            handleClick={() => handlePrevious()}
            disableGlowAnimation
            iconClassName='angle double left'
          />}
          <div>{`Schritt ${activeStep + 1} von ${steps.length}`}</div>
          <AnimatedArrow
            handleClick={() => handleNext()}
            iconClassName='angle double right'
            hoverText='Zum nächsten Schritt'
          />
        </StepText>}
      </div>
    
    </Container>
  </>
}
export default ForageProduction